import type {
  DestinatarioPendencia,
  PendenciaSecretariaItem,
  StatusPendencia,
} from '@/features/secretaria-arbitral/types';

import { podeMovimentar, podeResponder } from './pendenciasPolitica';
import { deduplicarRastro } from './rastroPendencia';

const DESTINATARIO_LABELS: Record<DestinatarioPendencia, string> = {
  REQUERENTE: 'Requerente',
  REQUERIDO: 'Requerido',
  ARBITRO: 'Árbitro',
  SECRETARIA: 'Secretaria',
};

export interface DestinatarioDoCard {
  pendenciaId: string;
  nome: string;
  papel: string;
  status: StatusPendencia;
}

export interface CardPendencia {
  chave: string;
  grupoCriacaoId: string | null;
  /** Uma linha por destinatário do mesmo ato de criação. */
  pendencias: PendenciaSecretariaItem[];
  representante: PendenciaSecretariaItem;
  destinatarios: DestinatarioDoCard[];
  /** Status distintos dentro do grupo: resolver uma irmã não resolve as outras (EDGE-19). */
  statusDistintos: StatusPendencia[];
  /** Ids que a aba pode movimentar agora — vazio esconde as ações do card. */
  idsMovimentaveis: string[];
  /** `false` para aviso interno (`destinatario: 'SECRETARIA'`) — esconde só o "Responder". */
  podeResponder: boolean;
  /** A resposta da parte que espera o "Aprovar"/"Recusar" da Secretaria, se houver. */
  emValidacao: PendenciaSecretariaItem | null;
}

/**
 * O concluso legado nasceu sem `destinatario` (a coluna não era gravada, e não houve backfill),
 * mas a FK do árbitro está lá e o backend a resolve. Sem esta queda para o `tipo`, duas linhas
 * do mesmo árbitro apareceriam lado a lado — uma "(Árbitro)", a outra sem papel nenhum. Parte
 * legada segue sem papel de propósito: a FK diz que é parte, não se é requerente ou requerido.
 */
function papelDe(pendencia: PendenciaSecretariaItem): string {
  if (pendencia.destinatario) return DESTINATARIO_LABELS[pendencia.destinatario];
  return pendencia.destinatarioResolvido?.tipo === 'ARBITRO' ? DESTINATARIO_LABELS.ARBITRO : '';
}

function destinatarioDe(pendencia: PendenciaSecretariaItem): DestinatarioDoCard {
  const papel = papelDe(pendencia);
  return {
    pendenciaId: pendencia.id,
    nome: pendencia.destinatarioResolvido?.nome ?? papel,
    papel: pendencia.destinatarioResolvido ? papel : '',
    status: pendencia.status,
  };
}

/**
 * Validação é por pendência, e o card agrupa o fan-out. Só quem nasce com `requerValidacao` chega a
 * `AGUARDANDO_VALIDACAO`, e isso é exclusivo do nó de flow — que cria uma linha, sem grupo. Num
 * grupo (inexistente hoje) os botões agiriam sobre uma irmã só, ao lado das ações das outras: o
 * card só oferece validação quando é a pendência inteira.
 */
function unicaEmValidacao(pendencias: PendenciaSecretariaItem[]): PendenciaSecretariaItem | null {
  const [unica] = pendencias;
  return pendencias.length === 1 && unica.status === 'AGUARDANDO_VALIDACAO' ? unica : null;
}

/**
 * O texto que a parte enviou: a transição para `AGUARDANDO_VALIDACAO` guarda a resposta.
 *
 * Sobre o rastro DEDUPLICADO: toda transição chega em par (a linha do serviço, com o texto, e a
 * muda do trigger), sem ordem garantida — no rastro cru a escolha podia cair na muda e o diálogo
 * validava sem mostrar a resposta. E pela data, não pela posição: com reenvio após recusa há uma
 * transição por resposta, e a ordem do array não é contrato do core (review do Claude no #1237).
 */
export function respostaEmValidacao(pendencia: PendenciaSecretariaItem): string | null {
  const transicoes = deduplicarRastro(pendencia.rastro).filter(
    (entrada) => entrada.tipo === 'STATUS' && entrada.statusNovo === 'AGUARDANDO_VALIDACAO',
  );
  if (transicoes.length === 0) return null;
  const maisRecente = transicoes.reduce((atual, candidata) =>
    Date.parse(candidata.dataEvento) >= Date.parse(atual.dataEvento) ? candidata : atual,
  );
  return maisRecente.descricao?.trim() || null;
}

/**
 * Um card por ATO de criação: o fan-out grava N linhas com o mesmo `grupoCriacaoId`, e
 * mostrá-las como N cards repetiria o mesmo pedido na tela uma vez por destinatário.
 *
 * `grupoCriacaoId` nulo é card individual — é o estado de todo o legado (a coluna nasceu
 * nullable, sem backfill), não uma exceção rara. A ordem do backend é preservada: o grupo
 * ocupa a posição do seu primeiro membro.
 *
 * `processoEncerrado` zera as ações de todos os cards: o core recusa movimentar pendência de
 * processo terminal (409 `PROCESSO_ENCERRADO`, A-18), e mostrar o botão só para ele falhar é
 * pior que não mostrar. Os cards continuam na lista — some a ação, não a leitura.
 */
export function agruparPendencias(
  items: PendenciaSecretariaItem[],
  { processoEncerrado = false }: { processoEncerrado?: boolean } = {},
): CardPendencia[] {
  const cards: CardPendencia[] = [];
  const porGrupo = new Map<string, CardPendencia>();

  for (const pendencia of items) {
    const existente = pendencia.grupoCriacaoId ? porGrupo.get(pendencia.grupoCriacaoId) : undefined;
    if (existente) {
      existente.pendencias.push(pendencia);
      continue;
    }
    const card: CardPendencia = {
      chave: pendencia.grupoCriacaoId ?? pendencia.id,
      grupoCriacaoId: pendencia.grupoCriacaoId,
      pendencias: [pendencia],
      representante: pendencia,
      destinatarios: [],
      statusDistintos: [],
      idsMovimentaveis: [],
      podeResponder: true,
      emValidacao: null,
    };
    if (pendencia.grupoCriacaoId) porGrupo.set(pendencia.grupoCriacaoId, card);
    cards.push(card);
  }

  return cards.map((card) => ({
    ...card,
    destinatarios: card.pendencias.map(destinatarioDe).filter((d) => d.nome.length > 0),
    statusDistintos: [...new Set(card.pendencias.map((p) => p.status))],
    idsMovimentaveis: processoEncerrado
      ? []
      : card.pendencias.filter(podeMovimentar).map((p) => p.id),
    podeResponder: !processoEncerrado && card.pendencias.every(podeResponder),
    emValidacao: unicaEmValidacao(card.pendencias),
  }));
}
